<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Number, default: null },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const store = useStore();
const { t } = useI18n();

const rootEl = ref(null);
const query = ref('');
const results = ref([]);
const selectedContact = ref(null);
const isOpen = ref(false);
let searchTimeout = null;
let latestSearchId = 0;

watch(query, q => {
  clearTimeout(searchTimeout);
  if (q.length < 2) {
    results.value = [];
    return;
  }
  searchTimeout = setTimeout(async () => {
    latestSearchId += 1;
    const id = latestSearchId;
    try {
      await store.dispatch('contacts/search', { search: q, page: 1 });
      if (id !== latestSearchId) return;
      results.value = (store.getters['contacts/getContacts'] || []).slice(
        0,
        10
      );
    } catch {
      results.value = [];
    }
  }, 300);
});

watch(
  () => props.modelValue,
  v => {
    if (v === null) {
      selectedContact.value = null;
      query.value = '';
      results.value = [];
    }
  }
);

const pick = contact => {
  selectedContact.value = contact;
  query.value = '';
  results.value = [];
  isOpen.value = false;
  emit('update:modelValue', contact.id);
};

const clear = () => {
  selectedContact.value = null;
  emit('update:modelValue', null);
};

const formatPhoneBR = value => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const local =
    digits.length >= 12 && digits.startsWith('55') ? digits.slice(2) : digits;
  if (local.length === 11)
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10)
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return local;
};

const handleClickOutside = e => {
  if (rootEl.value && !rootEl.value.contains(e.target)) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  clearTimeout(searchTimeout);
});
</script>

<template>
  <div ref="rootEl" class="relative">
    <div
      v-if="selectedContact"
      class="flex items-center gap-2 rounded border border-n-strong bg-n-surface-2 px-3 py-2 text-sm"
    >
      <span class="flex-1 truncate">
        {{ selectedContact.name }}
        <span class="text-n-slate-11">{{ '·' }}</span>
        {{ formatPhoneBR(selectedContact.phone_number) || '—' }}
      </span>
      <button
        type="button"
        class="text-n-slate-11 hover:text-n-slate-12"
        :aria-label="t('PAINEL.APPOINTMENTS.CANCEL')"
        @click="clear"
      >
        {{ '×' }}
      </button>
    </div>
    <input
      v-else
      v-model="query"
      type="text"
      :placeholder="placeholder"
      class="w-full rounded border border-n-strong px-3 py-2 text-sm"
      @input="isOpen = true"
      @keydown.esc="isOpen = false"
    />
    <ul
      v-if="isOpen && query.length >= 2"
      class="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded border border-n-strong bg-n-surface-2 shadow-lg"
    >
      <li
        v-for="contact in results"
        :key="contact.id"
        class="cursor-pointer px-3 py-2 text-sm hover:bg-n-slate-2"
        @click.prevent="pick(contact)"
      >
        {{ contact.name }}
        <span class="text-n-slate-11">{{ '·' }}</span>
        {{ formatPhoneBR(contact.phone_number) || '—' }}
      </li>
      <li v-if="results.length === 0" class="px-3 py-2 text-sm text-n-slate-11">
        {{ t('PAINEL.APPOINTMENTS.NO_RESULTS') }}
      </li>
    </ul>
  </div>
</template>
